/**
 * @fileoverview Separator component - Atomic UI element
 * 
 * A visual separator component (horizontal or vertical divider).
 * Built with Tailwind CSS.
 * 
 * @module ui/shared/atoms/Separator
 */

import * as React from "react";
import { cn } from "../utils/cn";

/**
 * Separator component props
 */
export interface SeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Orientation of the separator
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Whether the separator is decorative (aria-hidden)
   */
  decorative?: boolean;
}

/**
 * Separator component
 * 
 * A visual separator for dividing content sections.
 * 
 * @param {SeparatorProps} props - Separator props
 * @returns {JSX.Element} Separator element
 * 
 * @example
 * <Separator orientation="horizontal" />
 * 
 * @example
 * <Separator orientation="vertical" className="h-8" />
 */
const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role={decorative ? "none" : "separator"}
        aria-orientation={orientation}
        className={cn(
          "shrink-0 bg-gray-200",
          orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
          className
        )}
        {...props}
      />
    );
  }
);

Separator.displayName = "Separator";

export { Separator };

